/**
 * KnowledgeRecentRuns — vibe-designer 1.173.0 Phase 5β1 + 1.180.0 Phase 6.3.
 *
 * "Recent ingest runs" widget on the Knowledge Overview. Mounts BELOW the
 * IngestPanel result cards. Two data sources merge here:
 *   - `recentRuns.jobs` (Phase 5β1 polling at `/api/knowledge/ingest/recent`):
 *     historical snapshot fetched on mount + after each successful ingest.
 *   - `activityEvents` (Phase 6.3 SSE stream at `/api/knowledge/activity/stream`):
 *     live events as they fire (text/url/repo/image ingests).
 *
 * Merge strategy (R1 Conc 4 LOCK): Map-based dedup keyed by `job_id`; the
 * SSE event "wins" when both sources reference the same job_id (live data
 * is fresher than historical snapshot). Sort by timestamp descending.
 * Cap displayed rows at 50.
 *
 * Type chip per row distinguishes the event source: ingest / url / repo /
 * image. Polling rows (which don't carry an event_type) default to 'ingest'.
 */
import { useCallback, useEffect, useMemo } from 'react';
import {
  Loader2, RefreshCw, CheckCircle2, XCircle, AlertTriangle,
  FileText, Link2, GitBranch, Image as ImageIcon, Activity,
} from 'lucide-react';
import { useKnowledgeStore } from '@/stores/knowledgeStore';
import { useActivityStream } from '@/hooks/useActivityStream';
import { useShowSystemEvents } from '@/hooks/useShowSystemEvents';
import type {
  ActivityEvent, ActivityEventType, IngestResponse,
} from '@/services/knowledgeApi';

interface MergedRow {
  job_id: string;
  source_name: string;
  overall_status: 'completed' | 'partial' | 'failed' | 'processing';
  total_chunks: number;
  services: string[];
  event_type: ActivityEventType;
  timestamp: string;  // ISO; used only for sort
  error: string | null;
}

function rowFromIngestResponse(j: IngestResponse): MergedRow {
  return {
    job_id: j.job_id,
    source_name: j.source_name,
    overall_status: j.overall_status,
    total_chunks: j.per_service.reduce((acc, r) => acc + r.chunks_created, 0),
    services: j.per_service
      .filter((r) => r.status === 'completed' || r.status === 'partial')
      .map((r) => r.service),
    // job_id prefix → event_type heuristic for polling rows:
    //   ki- → ingest (also covers image ingests; SSE row would override)
    //   ku- → url failure
    //   kr- → repo aggregate
    event_type: (
      j.job_id.startsWith('kr-') ? 'repo' :
      j.job_id.startsWith('ku-') ? 'url' :
      'ingest'
    ),
    timestamp: j.finished_at || j.started_at,
    error: null,
  };
}

function rowFromActivityEvent(e: ActivityEvent): MergedRow {
  return {
    job_id: e.job_id || e.event_id,
    source_name: e.source_name || '(unknown)',
    overall_status: e.overall_status || 'failed',
    total_chunks: e.chunks_created,
    services: e.services,
    event_type: e.event_type,
    timestamp: e.timestamp,
    error: e.error,
  };
}

export function KnowledgeRecentRuns() {
  const recentRuns = useKnowledgeStore((s) => s.recentRuns);
  const isFetching = useKnowledgeStore((s) => s.isFetchingRecentRuns);
  const error = useKnowledgeStore((s) => s.recentRunsError);
  const fetchRecentRuns = useKnowledgeStore((s) => s.fetchRecentRuns);
  const activityEvents = useKnowledgeStore((s) => s.activityEvents);
  const pushActivityEvent = useKnowledgeStore((s) => s.pushActivityEvent);
  const [showSystemEvents, setShowSystemEvents] = useShowSystemEvents();

  useEffect(() => {
    void fetchRecentRuns();
  }, [fetchRecentRuns]);

  // Wire SSE → store. The hook handles reconnect + cleanup on unmount.
  const handleEvent = useCallback(
    (event: ActivityEvent) => pushActivityEvent(event),
    [pushActivityEvent],
  );
  useActivityStream(handleEvent);

  // R1 Conc 4 LOCK — Map-keyed merge; SSE row wins on collision.
  // Phase 6.3.1: filter out `system` rows unless `showSystemEvents` toggle is ON.
  const merged: MergedRow[] = useMemo(() => {
    const byKey = new Map<string, MergedRow>();
    for (const j of recentRuns?.jobs || []) {
      byKey.set(j.job_id, rowFromIngestResponse(j));
    }
    for (const e of activityEvents) {
      const key = e.job_id || e.event_id;
      byKey.set(key, rowFromActivityEvent(e));  // live wins on collision
    }
    return Array.from(byKey.values())
      .filter((r) => showSystemEvents || r.event_type !== 'system')
      .sort((a, b) => (a.timestamp > b.timestamp ? -1 : 1))
      .slice(0, 50);
  }, [recentRuns, activityEvents, showSystemEvents]);

  return (
    <section data-testid="knowledge-recent-runs-section">
      <header className="flex items-center justify-between mb-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-vibe-text-secondary">
          Recent ingests
        </h2>
        <div className="flex items-center gap-1">
          <button
            data-testid="knowledge-recent-runs-toggle-system"
            type="button"
            onClick={() => setShowSystemEvents(!showSystemEvents)}
            className={`px-2 py-1 text-xs rounded flex items-center gap-1 ${
              showSystemEvents
                ? 'bg-vibe-bg-tertiary text-vibe-text'
                : 'text-vibe-text-secondary hover:bg-vibe-bg-tertiary'
            }`}
            title={showSystemEvents
              ? 'Hide continuous_injector heartbeat events'
              : 'Show continuous_injector heartbeat events'}
            aria-pressed={showSystemEvents}
          >
            <Activity className="w-3 h-3" />
            <span>System</span>
          </button>
          <button
            data-testid="knowledge-recent-runs-refresh"
            type="button"
            onClick={() => void fetchRecentRuns()}
            disabled={isFetching}
            className="px-2 py-1 text-xs rounded text-vibe-text-secondary hover:bg-vibe-bg-tertiary disabled:opacity-50 flex items-center gap-1"
          >
            {isFetching ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            <span>Refresh</span>
          </button>
        </div>
      </header>

      {error && (
        <div
          data-testid="knowledge-recent-runs-error"
          className="rounded border border-vibe-error/30 bg-vibe-error/10 p-2 text-xs text-vibe-error"
        >
          {error.message}
        </div>
      )}

      {!error && merged.length === 0 && (
        <div
          data-testid="knowledge-recent-runs-empty"
          className="text-xs text-vibe-text-secondary py-2"
        >
          No ingests since this vibe-backend started.
        </div>
      )}

      {merged.length > 0 && (
        <ul className="space-y-1">
          {merged.map((row) => (
            <RunRow key={row.job_id} row={row} />
          ))}
        </ul>
      )}
    </section>
  );
}

const TYPE_LABELS: Record<ActivityEventType, string> = {
  ingest: 'TEXT',
  url: 'URL',
  repo: 'REPO',
  image: 'IMG',
  system: 'SYS',
};

const TYPE_ICONS: Record<ActivityEventType, JSX.Element> = {
  ingest: <FileText className="w-3 h-3" />,
  url: <Link2 className="w-3 h-3" />,
  repo: <GitBranch className="w-3 h-3" />,
  image: <ImageIcon className="w-3 h-3" />,
  system: <Activity className="w-3 h-3" />,
};

function RunRow({ row }: { row: MergedRow }) {
  const Icon =
    row.overall_status === 'completed' ? CheckCircle2 :
    row.overall_status === 'partial' ? AlertTriangle :
    row.overall_status === 'processing' ? Loader2 :
    XCircle;
  const iconCls =
    row.overall_status === 'completed' ? 'text-vibe-success' :
    row.overall_status === 'partial' ? 'text-vibe-warning' :
    row.overall_status === 'processing' ? 'text-vibe-accent animate-spin' :
    'text-vibe-error';
  const completedSvcs = row.services.join(', ');
  return (
    <li
      data-testid={`knowledge-recent-runs-row-${row.job_id}`}
      className="flex items-center gap-2 px-2 py-1.5 text-xs rounded hover:bg-vibe-bg"
    >
      <Icon className={`w-4 h-4 ${iconCls}`} />
      <span
        data-testid={`knowledge-recent-runs-type-chip-${row.job_id}`}
        className="px-1.5 py-0.5 text-xs rounded bg-vibe-bg-tertiary text-vibe-text-secondary uppercase font-mono flex items-center gap-1"
        title={`event type: ${row.event_type}`}
      >
        {TYPE_ICONS[row.event_type]}
        <span>{TYPE_LABELS[row.event_type]}</span>
      </span>
      <span className="font-mono text-vibe-text truncate flex-1" title={row.source_name}>
        {row.source_name}
      </span>
      <span className="text-vibe-text-secondary uppercase font-mono">
        {row.overall_status}
      </span>
      {row.total_chunks > 0 && (
        <span className="text-vibe-text-secondary tabular-nums">
          {row.total_chunks} chunks
        </span>
      )}
      {completedSvcs && (
        <span className="text-vibe-text-secondary truncate max-w-xs" title={completedSvcs}>
          → {completedSvcs}
        </span>
      )}
    </li>
  );
}
